// Preparação da foto no navegador: lê o horário de captura (EXIF DateTimeOriginal),
// redimensiona para no máximo 2560px e recomprime (~0,82). Tudo em memória.

export const MAX_SIDE = 2560;
export const QUALITY = 0.82;
export const MAX_BYTES = 15 * 1024 * 1024;

export interface PreparedPhoto {
  blob: Blob;
  contentType: 'image/jpeg' | 'image/webp' | 'image/heic';
  width: number | null;
  height: number | null;
  capturedAt: number;
}

/** Lê DateTimeOriginal (0x9003) do EXIF de um JPEG. Retorna epoch ms no relógio local do aparelho. */
export async function readExifDate(file: Blob): Promise<number | null> {
  try {
    const buf = await file.slice(0, 256 * 1024).arrayBuffer();
    const v = new DataView(buf);
    if (v.getUint16(0) !== 0xffd8) return null;
    let off = 2;
    while (off + 4 < v.byteLength) {
      const marker = v.getUint16(off);
      const size = v.getUint16(off + 2);
      if (marker === 0xffe1 && v.getUint32(off + 4) === 0x45786966) return parseTiff(v, off + 10);
      if ((marker & 0xff00) !== 0xff00) return null;
      off += 2 + size;
    }
  } catch {
    /* EXIF ilegível: usa o relógio */
  }
  return null;
}

function parseTiff(v: DataView, tiff: number): number | null {
  const le = v.getUint16(tiff) === 0x4949;
  const u16 = (o: number) => v.getUint16(o, le);
  const u32 = (o: number) => v.getUint32(o, le);
  const findTag = (ifd: number, tag: number) => {
    const n = u16(ifd);
    for (let i = 0; i < n; i++) {
      const e = ifd + 2 + i * 12;
      if (u16(e) === tag) return e;
    }
    return -1;
  };
  const ifd0 = tiff + u32(tiff + 4);
  const exifPtr = findTag(ifd0, 0x8769);
  if (exifPtr < 0) return null;
  const exifIfd = tiff + u32(exifPtr + 8);
  const e = findTag(exifIfd, 0x9003);
  if (e < 0) return null;
  const count = u32(e + 4);
  const at = tiff + u32(e + 8);
  let s = '';
  for (let i = 0; i < count - 1 && i < 19; i++) s += String.fromCharCode(v.getUint8(at + i));
  const m = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/.exec(s);
  if (!m) return null;
  const t = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]).getTime();
  return Number.isFinite(t) ? t : null;
}

let webpSupport: boolean | null = null;
async function canEncodeWebp() {
  if (webpSupport !== null) return webpSupport;
  const c = document.createElement('canvas');
  c.width = c.height = 2;
  const b = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/webp', 0.8));
  webpSupport = !!b && b.type === 'image/webp';
  return webpSupport;
}

async function decode(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if ('createImageBitmap' in window) {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
      return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
    } catch {
      /* cai para <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  const capturedAt = (await readExifDate(file)) ?? (file.lastModified && Date.now() - file.lastModified < 5 * 60_000 ? file.lastModified : Date.now());
  let decoded;
  try {
    decoded = await decode(file);
  } catch {
    // Navegador sem decodificador (ex.: HEIC fora do Safari): envia o original se couber.
    if (/heic|heif/i.test(file.type) && file.size <= MAX_BYTES) {
      return { blob: file, contentType: 'image/heic', width: null, height: null, capturedAt };
    }
    throw new Error('Não foi possível ler esta imagem. Tente tirar a foto de novo.');
  }
  try {
    const scale = Math.min(1, MAX_SIDE / Math.max(decoded.width, decoded.height));
    const w = Math.max(1, Math.round(decoded.width * scale));
    const h = Math.max(1, Math.round(decoded.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { alpha: false })!;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(decoded.source, 0, 0, w, h);
    const type = (await canEncodeWebp()) ? 'image/webp' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, type, QUALITY));
    canvas.width = canvas.height = 0; // libera memória no iOS
    if (!blob) throw new Error('Falha ao comprimir a foto.');
    if (blob.size > MAX_BYTES) throw new Error('A foto ficou maior que 15 MB.');
    return { blob, contentType: type, width: w, height: h, capturedAt };
  } finally {
    decoded.close();
  }
}
